import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { PropsWithChildren } from 'react';

import { Sxable } from 'library/types';

export interface InlineDisplayTextContainerProps {
	columnsCount: number;
	rowsCount: number;
	size?: 'small' | 'medium';
}

export const InlineDisplayTextContainer = ({
	children,
	columnsCount,
	rowsCount,
	size = 'medium',
	sx = [],
}: Sxable & PropsWithChildren<InlineDisplayTextContainerProps>) => (
	<Box
		sx={[
			(theme) => ({
				display: 'grid',
				gap: 2,

				'& p': {
					fontSize: size === 'small' ? '0.875rem' : '1.125rem',
				},
				'& span': {
					color: '#141414',
					fontSize: size === 'small' ? '0.875rem' : '1.125rem',
				},
				'& > *': {
					color: 'grey.700',
					display: 'grid',
					alignItems: 'baseline',
					gridTemplateColumns: '2fr 3fr',
					gap: 2,
					gridAutoRows: 'min-content',
				},

				[theme.breakpoints.down('md')]: {
					gridTemplateColumns: 'max-content 1fr',
					'& > *': {
						display: 'contents',
					},
				},

				[theme.breakpoints.up('md')]: {
					gridTemplateColumns: `repeat(${columnsCount}, 1fr)`,
					gridTemplateRows: `repeat(${rowsCount}, auto)`,
					gridAutoFlow: 'column',
				},

				[theme.breakpoints.only('xs')]: {
					gridTemplateColumns: '1fr 1fr',
				},
			}),
			...(Array.isArray(sx) ? sx : [sx]),
		]}>
		{children}
	</Box>
);

interface InlineDisplayValueProps {
	label: string;
	value: string | number | React.JSX.Element | null | undefined;
	loading?: boolean;
	hide?: boolean;
	defaultValue?: string;
}

export const InlineDisplayValue = ({
	label,
	value,
	loading = false,
	hide = false,
	defaultValue = '',
}: InlineDisplayValueProps) => {
	const displayComponent = loading ? (
		<Skeleton variant="rounded" />
	) : (
		value || defaultValue
	);
	return !hide ? (
		<Box>
			<Typography>{label}</Typography>
			<Typography component="span">
				{displayComponent != null &&
				typeof displayComponent === 'object'
					? displayComponent
					: Boolean(displayComponent)
						? (displayComponent as string)
						: '\u00A0'}
			</Typography>
		</Box>
	) : null;
};

interface InlineDisplayValueUnitProps extends InlineDisplayValueProps {
	unit: string;
	value: string | number | null | undefined;
}

// It present all data or default value. That means if value is null, undefined or empty unit is skipped
export const InlineDisplayValueUnit = ({
	value,
	unit,
	...rest
}: InlineDisplayValueUnitProps) => (
	<InlineDisplayValue
		value={value ? `${value.toString()} ${unit}` : null}
		{...rest}
	/>
);
